/**
 * Offline-first catalogue snapshot loader.
 *
 * A build-time-generated /data/snapshot-v1.json contains every event,
 * stage, genre, and artist the DB knows about. We load it once at app
 * startup; after that, every API call checks the snapshot first and only
 * falls through to the network for cache-misses (which shouldn't happen
 * for the stock catalogue).
 *
 * This makes the app work offline on first launch as long as the service
 * worker has the snapshot in its precache — which it does.
 */
import type { ArtistDTO, EventDTO, StageDTO } from "./api";

export type Snapshot = {
  version: number;
  generatedAt: string;
  events: EventDTO[];
  stages: Record<string, StageDTO[]>;
  genres: Record<string, string[]>;
  artists: Record<string, ArtistDTO[]>;
};

const SNAPSHOT_URL = "/data/snapshot-v1.json";

let cached: Snapshot | null = null;
let inflight: Promise<Snapshot | null> | null = null;

/**
 * Kick off the snapshot fetch. Safe to call many times — always returns
 * the same in-flight promise. Returns null if the snapshot can't be
 * fetched (e.g. truly offline on first ever launch with no SW cache).
 */
export function loadSnapshot(): Promise<Snapshot | null> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetch(SNAPSHOT_URL, { cache: "default" })
    .then(r => (r.ok ? (r.json() as Promise<Snapshot>) : null))
    .then(s => {
      cached = s;
      if (s && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("snapshot:loaded", { detail: s }));
      }
      return s;
    })
    .catch(() => null);
  return inflight;
}

/** Returns the snapshot synchronously if already loaded; else null. */
export function snapshotSync(): Snapshot | null {
  return cached;
}

/**
 * Force a fresh fetch, bypassing the HTTP cache. Used by the Refresh
 * Data button. Returns the new snapshot (or null on failure).
 */
export async function refreshSnapshot(): Promise<Snapshot | null> {
  try {
    const r = await fetch(`${SNAPSHOT_URL}?_t=${Date.now()}`, { cache: "reload" });
    if (!r.ok) return null;
    cached = (await r.json()) as Snapshot;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("snapshot:loaded", { detail: cached }));
    }
    return cached;
  } catch {
    return null;
  }
}

/**
 * Drop the service worker's api-* caches so the next fetch hits network.
 * Ignores failures (no SW, not a secure context, etc.).
 */
export async function purgeApiCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  const names = await caches.keys().catch(() => [] as string[]);
  await Promise.all(
    names
      .filter(n => n.startsWith("api-") || n === "google-fonts" || n === "deezer-photos")
      .map(n => caches.delete(n).catch(() => false)),
  );
}
